import React, { createContext, use, useContext, useMemo } from "react";
import type { StyleProp, ViewProps, ViewStyle } from "react-native";
import { SafeAreaInsetsContext, SafeAreaView, type Edge } from "react-native-safe-area-context";

/** True when the native host has run the root under a floating Liquid Glass tab bar. */
const LiquidGlassTabBarContext = createContext<boolean>(false);

/**
 * Bottom inset a tab root's scroll views must clear, in points. Zero unless the tab bar is Liquid
 * Glass.
 *
 * Reads the nearest safe area context rather than calling `useSafeAreaInsets`, which throws when a
 * root has no `SafeAreaProvider`. Treating a missing provider as "not extended" keeps the layout on
 * its pre-Liquid-Glass path instead of dropping the bottom edge with no inset to replace it.
 *
 * The safe area context is only read when the bar is Liquid Glass (`use` may be called
 * conditionally), so roots that aren't extended don't depend on it at all.
 */
function useScrollContentBottomInset(): number {
  const enabled = useContext(LiquidGlassTabBarContext);
  if (!enabled) {
    return 0;
  }
  return use(SafeAreaInsetsContext)?.bottom ?? 0;
}

export interface LiquidGlassTabBarProviderProps {
  /**
   * `nativeContext.liquidGlassTabBar` — true when the native host ran this root to the bottom edge
   * of the screen because the tab bar is floating Liquid Glass. Only iOS 26 hosts on the Liquid
   * Glass proctor bucket report true, so there is no platform check to make here.
   */
  readonly enabled: boolean | undefined;
  readonly children: React.ReactNode;
}

/**
 * RN-8430: Mounted once for every React Native root by `withProviderTracking`, so tab roots don't
 * mount it themselves. Only tab roots are ever reported as extended, so it is inert everywhere else.
 *
 * Liquid Glass floats the bottom tab bar over the page, and the native host responds by running the
 * root view to the bottom edge of the screen. The root's bottom safe area inset then covers the tab
 * bar, and the page has to consume it as scroll *content* inset rather than as `SafeAreaView`
 * padding — pad the view instead and the scroll view stops short of the bar, leaving an opaque band
 * behind the glass instead of page content.
 *
 * `TabRootSafeArea` reads this to drop its bottom edge; `useTabBarScrollInsets` reads it to inset
 * scroll content. Both are no-ops when the host reports nothing.
 */
export function LiquidGlassTabBarProvider({ enabled, children }: LiquidGlassTabBarProviderProps) {
  return (
    <LiquidGlassTabBarContext.Provider value={enabled === true}>
      {children}
    </LiquidGlassTabBarContext.Provider>
  );
}

export interface TabRootSafeAreaProps {
  /** The safe area edges this root insets when the tab bar is not Liquid Glass. */
  readonly edges: readonly Edge[];
  readonly style?: StyleProp<ViewStyle>;
  readonly onLayout?: ViewProps["onLayout"];
  readonly children: React.ReactNode;
}

/**
 * Drop-in replacement for a tab root's `SafeAreaView`. Keeps `edges` as given, minus `bottom` when
 * the root runs under a floating Liquid Glass tab bar — in which case the bottom inset reaches the
 * page's scroll views through `useTabBarScrollInsets` instead.
 */
export function TabRootSafeArea({ edges, style, onLayout, children }: TabRootSafeAreaProps) {
  const scrollContentBottomInset = useScrollContentBottomInset();
  const resolvedEdges = useMemo(
    () => (scrollContentBottomInset > 0 ? edges.filter((edge) => edge !== "bottom") : edges),
    [edges, scrollContentBottomInset],
  );

  return (
    <SafeAreaView style={style} edges={resolvedEdges} onLayout={onLayout}>
      {children}
    </SafeAreaView>
  );
}

/** Bottom inset the page's scroll content must clear. Zero unless the tab bar is Liquid Glass. */
export function useTabBarContentBottomInset(): number {
  return useScrollContentBottomInset();
}

export interface TabBarScrollInsets {
  readonly contentInset: { readonly bottom: number };
  readonly scrollIndicatorInsets: { readonly bottom: number };
}

/**
 * Props to spread onto a tab root's scroll view (`ScrollView`, `FlatList`, …) so its last item
 * clears the floating tab bar.
 *
 * Uses iOS scroll content insets rather than content-container padding so it composes with whatever
 * `paddingBottom` the list already styles, instead of overriding it.
 */
export function useTabBarScrollInsets(): TabBarScrollInsets {
  const bottom = useScrollContentBottomInset();

  return useMemo(() => ({ contentInset: { bottom }, scrollIndicatorInsets: { bottom } }), [bottom]);
}
